/**
 * Admin Transaction Logs API
 * Base: /api/v1/admin/transaction-logs
 */
import api from './api';

const BASE = '/admin/transaction-logs';

export interface TransactionLogSummary {
  id: number;
  order_id: number | null;
  restaurant_id: number | null;
  user_id: number | null;
  provider: string;
  provider_transaction_id: string | null;
  reference_id: string | null;
  transaction_type: string;
  payment_method: string | null;
  channel_code: string | null;
  currency: string;
  amount: number | null;
  platform_fee_amount: number | null;
  merchant_amount: number | null;
  internal_status: string | null;
  gateway_status: string | null;
  webhook_received_at: string | null;
  created_at: string;
  updated_at: string;
  order_code: string | null;
  order_status?: string | null;
  order_refund_status?: string | null;
  merchant_name: string | null;
  customer_name: string | null;
  customer_phone: string | null;
}

export interface TransactionLogDetail extends TransactionLogSummary {
  order_status?: string | null;
  order_total?: number | null;
  order_delivery_fee?: number | null;
  order_discount?: number | null;
  order_payment_method?: string | null;
  merchant_address?: string | null;
  customer_email?: string | null;
  callback_url?: string | null;
  checkout_url?: string | null;
  deeplink?: string | null;
  qr_string?: string | null;
  sub_account_id?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  request_payload?: unknown;
  response_payload?: unknown;
  webhook_payload?: unknown;
  metadata?: unknown;
  amountMatch?: boolean | null;
  riskFlags?: Array<{ type: string; message: string }>;
}

export interface TransactionLogEvent {
  id: number;
  transaction_log_id: number;
  event_type: string;
  event_message: string | null;
  actor_type: string | null;
  payload: unknown;
  created_at: string;
}

export interface TransactionLogsSummary {
  total: number;
  success: number;
  failed: number;
  refunded: number;
  volume: number;
  pendingOver15Min: number;
}

export interface TransactionLogsFiltersValues {
  q?: string;
  from?: string;
  to?: string;
  transactionType?: string;
  paymentMethod?: string;
  internalStatus?: string;
  gatewayStatus?: string;
  merchantId?: number;
  amountMin?: number;
  amountMax?: number;
  hasWebhook?: boolean;
  failedOnly?: boolean;
  refundedOnly?: boolean;
}

export interface TransactionLogsListParams {
  q?: string;
  keyword?: string;
  from?: string;
  to?: string;
  transactionType?: string;
  paymentMethod?: string;
  internalStatus?: string;
  gatewayStatus?: string;
  merchantId?: number;
  amountMin?: number;
  amountMax?: number;
  hasWebhook?: boolean;
  failedOnly?: boolean;
  refundedOnly?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

export interface TransactionLogsListResponse {
  success: boolean;
  data?: TransactionLogSummary[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  message?: string;
}

/** GET /admin/transaction-logs/summary */
export async function fetchTransactionLogsSummary(params?: {
  from?: string;
  to?: string;
}): Promise<{
  success: boolean;
  data?: TransactionLogsSummary;
}> {
  const query: Record<string, string> = {};
  if (params?.from) query.from = params.from;
  if (params?.to) query.to = params.to;
  const res = await api.get(`${BASE}/summary`, { params: query });
  return res.data;
}

/** GET /admin/transaction-logs */
export async function fetchTransactionLogs(
  params: TransactionLogsListParams
): Promise<TransactionLogsListResponse> {
  const query: Record<string, string | number | boolean | undefined> = {};
  if (params.q) query.q = params.q;
  if (params.keyword) query.keyword = params.keyword;
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  if (params.transactionType) query.transactionType = params.transactionType;
  if (params.paymentMethod) query.paymentMethod = params.paymentMethod;
  if (params.internalStatus) query.internalStatus = params.internalStatus;
  if (params.gatewayStatus) query.gatewayStatus = params.gatewayStatus;
  if (params.merchantId != null) query.merchantId = params.merchantId;
  if (params.amountMin != null) query.amountMin = params.amountMin;
  if (params.amountMax != null) query.amountMax = params.amountMax;
  if (params.hasWebhook != null) query.hasWebhook = params.hasWebhook;
  if (params.failedOnly) query.failedOnly = params.failedOnly;
  if (params.refundedOnly) query.refundedOnly = params.refundedOnly;
  if (params.page) query.page = params.page;
  if (params.limit) query.limit = params.limit;
  if (params.sortBy) query.sortBy = params.sortBy;
  if (params.sortOrder) query.sortOrder = params.sortOrder;

  const res = await api.get<TransactionLogsListResponse>(BASE, {
    params: query,
  });
  return res.data;
}

/** GET /admin/transaction-logs/:id */
export async function fetchTransactionLogDetail(
  id: number
): Promise<{ success: boolean; data?: TransactionLogDetail }> {
  const res = await api.get(`${BASE}/${id}`);
  return res.data;
}

/** GET /admin/transaction-logs/:id/events */
export async function fetchTransactionLogEvents(
  id: number
): Promise<{ success: boolean; data?: TransactionLogEvent[] }> {
  const res = await api.get(`${BASE}/${id}/events`);
  return res.data;
}
